import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../hooks/useSupabaseAuth"

interface PDFViewerModalProps {
  contentId: number;
  title: string;
  onClose: () => void;
}

interface Bookmark {
  id: string;
  page_number: number;
  label: string;
  notes?: string;
  created_at: string;
}

interface ReadingProgress {
  current_page: number;
  total_pages: number;
  progress_percentage: number;
}

export const PDFViewerModal: React.FC<PDFViewerModalProps> = ({
  contentId,
  title,
  onClose,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newBookmarkLabel, setNewBookmarkLabel] = useState("");
  const [newBookmarkNotes, setNewBookmarkNotes] = useState("");
  const [progress, setProgress] = useState<ReadingProgress>({
    current_page: 1,
    total_pages: 0,
    progress_percentage: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<"bookmarks" | "notes" | "search">(
    "bookmarks"
  );
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    // Only fetch content if user is authenticated
    if (authLoading) {
      return; // Wait for auth check to complete
    }
    
    if (!isAuthenticated) {
      setError('You must be signed in to view content. Please sign in and try again.');
      setLoading(false);
      return;
    }
    
    fetchPDFContent();
    fetchBookmarks();
    fetchReadingProgress();
  }, [contentId, isAuthenticated, authLoading]);

  const fetchPDFContent = async () => {
    // Check authentication before making API call
    if (!isAuthenticated) {
      setError('You must be signed in to view content. Please sign in and try again.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/client/view-content/${contentId}`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to load PDF content");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error("PDF loading error:", err);
      setError(err instanceof Error ? err.message : "Failed to load PDF");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookmarks = async () => {
    if (!isAuthenticated) {
      return; // Don't fetch bookmarks if not authenticated
    }

    try {
      const response = await fetch(
        `/api/client/content/${contentId}/bookmarks`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBookmarks(data);
      }
    } catch (err) {
      console.error("Error fetching bookmarks:", err);
    }
  };

  const fetchReadingProgress = async () => {
    if (!isAuthenticated) {
      return; // Don't fetch progress if not authenticated
    }

    try {
      const response = await fetch(
        `/api/client/content/${contentId}/progress`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setProgress(data);
        setCurrentPage(data.current_page || 1);
        setTotalPages(data.total_pages || 0);
      }
    } catch (err) {
      console.error("Error fetching reading progress:", err);
    }
  };

  const updateReadingProgress = async (page: number) => {
    if (!isAuthenticated) {
      return; // Don't update progress if not authenticated
    }

    try {
      const progressPercentage = totalPages > 0 ? (page / totalPages) * 100 : 0;

      await fetch(`/api/client/content/${contentId}/progress`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current_page: page,
          total_pages: totalPages,
          progress_percentage: progressPercentage,
        }),
      });

      setProgress((prev) => ({
        ...prev,
        current_page: page,
        progress_percentage: progressPercentage,
      }));
    } catch (err) {
      console.error("Error updating reading progress:", err);
    }
  };

  const addBookmark = async () => {
    if (!newBookmarkLabel.trim()) return;
    
    if (!isAuthenticated) {
      return; // Don't add bookmarks if not authenticated
    }

    try {
      const response = await fetch(
        `/api/client/content/${contentId}/bookmarks`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page_number: currentPage,
            label: newBookmarkLabel.trim(),
            notes: newBookmarkNotes.trim() || undefined,
          }),
        }
      );

      if (response.ok) {
        const newBookmark = await response.json();
        setBookmarks((prev) => [...prev, newBookmark]);
        setNewBookmarkLabel("");
        setNewBookmarkNotes("");
      }
    } catch (err) {
      console.error("Error adding bookmark:", err);
    }
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    updateReadingProgress(page);
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = `${title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  // Show authentication loading state
  if (authLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white max-w-md w-full mx-4 rounded-lg shadow-2xl p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying access...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show authentication error if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white max-w-md w-full mx-4 rounded-lg shadow-2xl p-8">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600 mb-4">You must be signed in to view content.</p>
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-full h-full max-w-7xl max-h-[90vh] mx-4 my-8 rounded-lg shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-900 truncate">
              {title}
            </h2>
            <div className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress.progress_percentage}%` }}
                />
              </div>
              <span className="text-xs text-gray-500">
                {Math.round(progress.progress_percentage)}%
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
            >
              Download
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
            >
              Close
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* PDF Viewer */}
          <div className="flex-1 flex flex-col">
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading PDF...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-red-500 mb-4">
                    <svg
                      className="w-16 h-16 mx-auto"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Error Loading PDF
                  </h3>
                  <p className="text-gray-600">{error}</p>
                </div>
              </div>
            ) : (
              <iframe
                ref={iframeRef}
                src={pdfUrl || ""}
                className="flex-1 w-full border-none"
                title={title}
              />
            )}

            {/* Page Navigation */}
            <div className="flex items-center justify-center gap-4 p-4 border-t border-gray-200">
              <button
                onClick={() => goToPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-1 bg-gray-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 bg-gray-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-80 border-l border-gray-200 flex flex-col">
            {/* Sidebar Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab("bookmarks")}
                className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 ${
                  activeTab === "bookmarks"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Bookmarks
              </button>
              <button
                onClick={() => setActiveTab("notes")}
                className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 ${
                  activeTab === "notes"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Notes
              </button>
              <button
                onClick={() => setActiveTab("search")}
                className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 ${
                  activeTab === "search"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Search
              </button>
            </div>

            {/* Sidebar Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {activeTab === "bookmarks" && (
                <div className="space-y-4">
                  {/* Add Bookmark */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Add Bookmark
                    </h4>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Bookmark label"
                        value={newBookmarkLabel}
                        onChange={(e) => setNewBookmarkLabel(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                      <textarea
                        placeholder="Notes (optional)"
                        value={newBookmarkNotes}
                        onChange={(e) => setNewBookmarkNotes(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                      <button
                        onClick={addBookmark}
                        className="w-full px-3 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
                      >
                        Add Bookmark
                      </button>
                    </div>
                  </div>

                  {/* Bookmarks List */}
                  <div className="space-y-2">
                    {bookmarks.length === 0 ? (
                      <p className="text-gray-500 text-sm">No bookmarks yet</p>
                    ) : (
                      bookmarks.map((bookmark) => (
                        <div
                          key={bookmark.id}
                          className="bg-white border border-gray-200 rounded-lg p-3 cursor-pointer hover:bg-gray-50"
                          onClick={() => goToPage(bookmark.page_number)}
                        >
                          <div className="flex items-center justify-between">
                            <h5 className="font-medium text-gray-900 text-sm">
                              {bookmark.label}
                            </h5>
                            <span className="text-xs text-gray-500">
                              Page {bookmark.page_number}
                            </span>
                          </div>
                          {bookmark.notes && (
                            <p className="text-xs text-gray-600 mt-1">
                              {bookmark.notes}
                            </p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {activeTab === "notes" && (
                <div className="space-y-4">
                  <p className="text-gray-500 text-sm">
                    Note-taking feature coming soon
                  </p>
                </div>
              )}

              {activeTab === "search" && (
                <div className="space-y-4">
                  <div>
                    <input
                      type="text"
                      placeholder="Search in PDF..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <p className="text-gray-500 text-sm">
                    Full-text search feature coming soon
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
